import { useEffect, useState } from 'react';

import { getMCQWinners } from '@services/presentation-live';
import { getQuizScoresPaginated, getFeaturesCommentDetails } from '@services/youtube-platform.service';

import Button from '@atoms/Button.new';
import Tooltip from '@atoms/new/tooltip';

import { Modal, ModalBody, ModalContent, ModalHeader } from '@atoms/new/modal';
import {
  WarningCircleIcon as ErrorIcon,
  ImageIcon,
  LightbulbIcon as LightbulbOutlinedIcon
} from '@phosphor-icons/react';

const quizTypes = ['multipleChoice', 'quiz'];

export const SecondaryWorkFlowContent = ({
  colors,
  linkTemp,
  streamId,
  setShowImage,
  createImageFn,
  onSummaryOpen,
  featureId = '',
  currentRosResult,
  isPreview = false,
  currentRosFeature,
  handleStopFeature,
  secondaryWorkflowOpen,
  setSecondaryWorkflowOpen,
  createWinningWheelForCurrentInteraction,
}) => {
  const [showFailedToGenerateModal, setFailedToGenerateModal] = useState(false);
  const [isCreatingWheel, setIsCreatingWheel] = useState(false);

  useEffect(() => {
    if (!secondaryWorkflowOpen) return;
    setFailedToGenerateModal(false);
  }, [secondaryWorkflowOpen]);

  const { type, setting } = currentRosFeature;

  const handleCreateWheel = async () => {
    let response;
    if (isPreview) return;
    if (type === 'multipleChoice') {
      response = await getMCQWinners(streamId, setting.wheelMcqLimit, featureId);
    } else if (type === 'quiz') {
      response = await getQuizScoresPaginated(streamId, {
        filter: 'all',
        quizId: featureId,
        questionNumber: currentRosFeature?.question_number
      });
    } else {
      response = await getFeaturesCommentDetails(streamId, featureId, setting.wheelShowUser);
    }

    if (isCreatingWheel) return;

    if (!response?.status || response?.entity?.length === 0) {
      setFailedToGenerateModal(true);
      return;
    }
    try {
      setIsCreatingWheel(true);
      handleStopFeature();
      await createWinningWheelForCurrentInteraction({
        featureId,
        featureType: type,
        currentRosResult
      });
    } finally {
      setIsCreatingWheel(false);
    }
  };

  return <>
    <div className="flex items-center justify-between mb-3 z-20">
      <div className='flex gap-2'>
        <Button
          size='lg'
          variant='custom'
          onClick={onSummaryOpen}
          className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} z-20 h-12!`}
          roundedClass='rounded-xl'
          addonLeft={<LightbulbOutlinedIcon size={16} />}
        >
                    AI summary
        </Button>

        <Button
          onClick={async () => {
            const questionId = currentRosFeature?.id;
            handleStopFeature();

            setShowImage(true);
            await createImageFn({
              questionId,
              streamId,
              type: currentRosFeature?.type,
              layoutMode: linkTemp.layoutMode,
              engagementId: featureId,
            });
          }}
          size='lg'
          variant='custom'
          className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} z-20 h-12!`}
          addonLeft={<ImageIcon size={16} />}
          roundedClass='rounded-xl'
        >
                    AI image
        </Button>
      </div>
    </div>

    <div className={`${colors.containerColor} rounded-xl cursor-pointer h-fit !shadow-none`}>
      {!isPreview ? <SecondaryWheel
        colors={colors}
        isPreview={isPreview}
        isCreatingWheel={isCreatingWheel}
        handleCreateWheel={handleCreateWheel}
        type={type}
      /> : <Tooltip
        placement="right"
        content="New Winning Wheel: Available on the live presentation page."
      >
        <div
          className={'group'}
        >
          <SecondaryWheel
            colors={colors}
            isPreview={isPreview}
            isCreatingWheel={isCreatingWheel}
            handleCreateWheel={handleCreateWheel}
            type={type}
          />
        </div>
      </Tooltip>}
    </div >

    {showFailedToGenerateModal && <>
      <Modal
        isOpen={showFailedToGenerateModal}
        onClose={() => setFailedToGenerateModal(false)}
        onOpenChange={() => setFailedToGenerateModal(false)}
        size='lg'
        className='h-fit'
      >
        <ModalContent>
          <ModalHeader>
            <div className='flex items-center gap-2'>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className='p-20 flex flex-col items-center justify-center gap-4 w-full h-full'>
              <ErrorIcon className='dark:text-white text-grey-900' />
              <p className='dark:text-white text-grey-900 text-xl font-normal text-center'>
                {quizTypes.includes(type) ? 'There are no participants who gave the correct answer.' : <>No responses yet. Ask participants to<br />
                                    type in the chat and try again.</>}
              </p>

              <Button
                variant='secondary'
                onPress={() => {
                  setFailedToGenerateModal(false);
                }}
              >
                                Close
              </Button>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>}
  </>;
};

const SecondaryWheel = ({
  colors,
  isPreview,
  isCreatingWheel,
  handleCreateWheel,
  type,
}) => {
  return <div
    className={`group flex items-center justify-start rounded-xl h-fit gap-2 ${colors.hoverBg} p-[12px] ${isCreatingWheel || isPreview ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
    onClick={async () => {
      await handleCreateWheel();
    }}
  >
    <img
      src="/images/ros/interactions/wheel.svg"
      className={isCreatingWheel ? 'animate-spin' : ''}
    />
    <div className={`flex flex-col ${colors.hoverBg} ${colors.textColor}`}>
      <p className='text-sm font-semibold'>Winning Wheel</p>
      <p className='text-sm'>{quizTypes.includes(type) ? 'Spin to pick a winner from correct entries' : 'Spin the spinner to select a participant'}</p>
    </div>
  </div>;
};
